import clsx from "clsx";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { FilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { PendingFilters } from "web/react/components/feeds-filters/pending-filters";
import { FilterOptionGroup } from "web/react/components/filter-option-group/filter-option-group";
import {
    extractFilterStateCount,
    useFilterContext,
} from "web/react/components/filters-emo/filter-container-emo";
import { FilterContentContainer } from "web/react/components/filters-emo/filter-content-container/filter-content-container";
import { FILTER_SECTION } from "web/react/components/filters-emo/filter-utils";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";

import { ClearFilterButton } from "web/react/components/filters-emo/clear-filter-button/clear-filter-button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { SortOptionSerializer } from "web/types/serializers";
import {
    FilteredScrollAreaFilterOptionGroup,
    UniversalFilterOption,
    UniversalFilterSection,
} from "web/types/universal-filters";
import * as styles from "./more-filters.css";

interface MoreFiltersProps
    extends Pick<FilterManager, "lazyFacetFetch" | "lazyFacetIsLoading" | "clearSelectedFilters"> {
    items: UniversalFilterSection[];
    sortOptions?: SortOptionSerializer[];
}

interface MoreFiltersChildrenProps
    extends Pick<FilterManager, "lazyFacetFetch" | "lazyFacetIsLoading" | "clearSelectedFilters"> {
    items: UniversalFilterSection[];
    onClick: (any) => void;
    pendingFilterInfo: PendingFilters;
    sortOptions?: SortOptionSerializer[];
    onClearAllClick?: () => void;
}

interface FilterListProps extends MoreFiltersChildrenProps {
    filterSectionList: UniversalFilterSection[];
    onClearClick: (sectionToClear?: string) => void;
    updateFromServer: () => void;
}

export function CheckboxFilters({
    items,
    onClick,
    ...props
}: MoreFiltersChildrenProps): JSX.Element {
    const { isDesktopViewport } = useDomViewport();

    return (
        <div className={clsx({ [styles.sectionColumn["sm"]]: isDesktopViewport })}>
            {items.map((item) => {
                const hasOptions = item.option_groups[0].options.length > 0;
                return (
                    hasOptions && (
                        <View key={item.id} marginBottom={"sm"}>
                            <Heading textStyle={"subhead"}>{item.title}</Heading>
                            <ol>
                                {item.option_groups[0].options.map((item, i) => {
                                    return (
                                        <li key={i}>
                                            <CheckboxFilterItem
                                                {...{
                                                    item,
                                                    onClick,
                                                    ...props,
                                                }}
                                            />
                                        </li>
                                    );
                                })}
                            </ol>
                        </View>
                    )
                );
            })}
        </div>
    );
}

function FilterGroup({
    item,
    clearSelectedFilters,
    ...props
}: { item: UniversalFilterSection; hideSearch?: boolean } & Omit<
    MoreFiltersProps,
    "items"
>): JSX.Element {
    const optionGroup = item.option_groups[0] as FilteredScrollAreaFilterOptionGroup;
    const detailedCount = useSelector(extractFilterStateCount);
    const filterCount = detailedCount[item.id]?.breadcrumbs.length;

    return (
        <>
            <div className={styles.filterGroupHeaderContainer}>
                <HStack spacing="xxxs" justify={"space-between"}>
                    <HStack spacing="xxxs">
                        <Heading textStyle={"subhead"}>{item.title}</Heading>
                        <Text
                            className={styles.appliedFilterCount}
                            as={"span"}
                            textStyle={"body-2"}
                        >
                            {`(${filterCount})`}
                        </Text>
                    </HStack>
                    <ClearFilterButton
                        className={styles.clearButton}
                        active={filterCount !== 0}
                        onClick={() => {
                            filterCount > 0 && clearSelectedFilters(item.id);
                        }}
                    />
                </HStack>
            </div>
            <FilterOptionGroup
                key={item.id}
                isVisible
                {...{
                    optionGroup,
                    ...props,
                }}
            />
        </>
    );
}

const getSelectedFiltersLabel = (
    options: UniversalFilterOption[],
    flatFilters: any[] = []
): string => {
    const checked = options.map(({ checked, label }) => {
        if (!!checked) {
            return label;
        }
    });
    const checkedOption = options.find((option) => {
        if (!!option.checked) {
            return option;
        }
    });
    options.length > 0 && flatFilters.push(checked.filter((n) => n).join(", "));
    if (checkedOption && "children" in checkedOption && checkedOption.children.length > 0) {
        getSelectedFiltersLabel((checkedOption as any)?.children, flatFilters);
    }
    return flatFilters.filter((n) => n).join(", ");
};

function FilterList({
    items,
    filterSectionList,
    onClearClick,
    onClearAllClick,
    ...props
}: FilterListProps): JSX.Element {
    const [filterSection, setFilterSection] = useState<UniversalFilterSection | null>(null);
    const filterCountHolder = useSelector(extractFilterStateCount);
    const filterCount = !!filterSection?.id
        ? filterCountHolder[filterSection.id].breadcrumbs.length
        : null;
    const allFiltersCount = Object.values(filterCountHolder).reduce(
        (acc, { breadcrumbs }) => acc + breadcrumbs.length,
        0
    );
    const { isMobileViewport } = useDomViewport();
    const size = isMobileViewport ? "sm" : "md";

    return (
        <>
            {filterSection?.id && (
                <span className={styles.chevron}>
                    <button
                        data-test-id="return-more-filters"
                        onClick={() => {
                            setFilterSection(null);
                            if (props.pendingFilterInfo.hasPending()) {
                                props.updateFromServer();
                            }
                        }}
                        className={styles.chevronButton[size]}
                        type="button"
                    >
                        <SVGIcon name="chevron-thin" rotate={90} />
                    </button>
                </span>
            )}
            <div className={styles.wrapper["sm"]}>
                <VStack spacing="md">
                    {filterSection?.id ? (
                        <>
                            <ClearFilterButton
                                className={styles.clearButton}
                                active={!!filterCount}
                                onClick={() => {
                                    filterSection && onClearClick(filterSection.id);
                                }}
                            />
                            {getFilterContentContainer(filterSection?.id, props)}
                        </>
                    ) : (
                        <>
                            <button onClick={onClearAllClick} disabled={allFiltersCount <= 0}>
                                <Text
                                    textStyle="footnote"
                                    className={
                                        styles.clearAllButton[
                                            allFiltersCount > 0 ? "active" : "inactive"
                                        ]
                                    }
                                >
                                    {
                                        // Clear All
                                        gettext("feed.filters.mobile_clear_all_btn_text")
                                    }
                                </Text>
                            </button>
                            {items.map((item) => {
                                const selectedSection = filterSectionList.find(
                                    (section) => section.id === item.id
                                );
                                if (selectedSection) {
                                    const checkedChildren = item.option_groups
                                        .map((group) => {
                                            const selectedFilterLabel = getSelectedFiltersLabel(
                                                group.options
                                            );
                                            return group.id === "option-group-final-price-from"
                                                ? selectedFilterLabel + " - "
                                                : selectedFilterLabel;
                                        })
                                        .filter((n) => n);
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => {
                                                analytics.event(
                                                    "more-filters-section",
                                                    "click",
                                                    selectedSection.title
                                                );
                                                setFilterSection(selectedSection || null);
                                            }}
                                        >
                                            <HStack justify="space-between">
                                                <div className={styles.textWrapper}>
                                                    <Text textStyle="body-3">
                                                        {selectedSection
                                                            ? selectedSection.title
                                                            : item.title}
                                                    </Text>
                                                    {checkedChildren.length > 0 && (
                                                        <Text
                                                            className={styles.selectedFiltersText}
                                                            textStyle="body-3"
                                                        >
                                                            {checkedChildren}
                                                        </Text>
                                                    )}
                                                </div>
                                                <SVGIcon
                                                    className={styles.itemChevron}
                                                    name="chevron"
                                                />
                                            </HStack>
                                        </div>
                                    );
                                }
                            })}
                        </>
                    )}
                </VStack>
            </div>
        </>
    );
}

function FilterDetailedDrawer({ items, onClick, ...props }: MoreFiltersChildrenProps): JSX.Element {
    const firstColumn: UniversalFilterSection[] = [];
    const secondColumn: UniversalFilterSection[] = [];
    let materialColumn: UniversalFilterSection | null = null;
    let shopColumn: UniversalFilterSection | null = null;

    items.forEach((item) => {
        switch (item.id) {
            case FILTER_SECTION.genderFilter:
            case FILTER_SECTION.shippingFilter:
                firstColumn.push(item);
                break;
            case FILTER_SECTION.saleFilter:
                secondColumn.push(item);
                break;
            case FILTER_SECTION.materialFilter:
                materialColumn = item;
                break;
            case FILTER_SECTION.retailerFilter:
                shopColumn = item;
                break;
            default:
                break;
        }
    });

    return (
        <View className={styles.wrapper["lg"]} marginTop="md" width="full">
            {firstColumn.length > 0 && (
                <CheckboxFilters {...{ items: firstColumn, onClick, ...props }} />
            )}
            {secondColumn.length > 0 && (
                <CheckboxFilters {...{ items: secondColumn, onClick, ...props }} />
            )}
            {materialColumn && (
                <div className={styles.sectionColumn["md"]}>
                    <FilterGroup
                        {...{ item: materialColumn, onClick, showSearch: false, ...props }}
                    />
                </div>
            )}
            {shopColumn && (
                <div className={styles.sectionColumn["lg"]}>
                    <FilterGroup {...{ item: shopColumn, onClick, ...props }} />
                </div>
            )}
        </View>
    );
}

function getFilterContentContainer(filterId, props): JSX.Element {
    return <FilterContentContainer activeFilterId={filterId} {...props} />;
}

export function MoreFilters({ items, ...props }: MoreFiltersProps): JSX.Element {
    const {
        displayedFilters,
        onFilterItemClick,
        pendingFilterInfo,
        onClearClick,
        updateFromServer,
    } = useFilterContext();

    const { isDesktopViewport } = useDomViewport();

    return isDesktopViewport ? (
        <FilterDetailedDrawer
            items={items}
            onClick={onFilterItemClick}
            pendingFilterInfo={pendingFilterInfo}
            {...props}
        />
    ) : (
        <FilterList
            items={items}
            onClick={onFilterItemClick}
            pendingFilterInfo={pendingFilterInfo}
            filterSectionList={displayedFilters.filter_section_list}
            onClearClick={onClearClick}
            updateFromServer={updateFromServer}
            {...props}
        />
    );
}
